import React, { useCallback, useContext, useEffect, useState } from 'react'
import { generatePath } from 'react-router-dom'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { SortDescriptor } from '@progress/kendo-data-query'
import { CompositeFilterDescriptor } from '@progress/kendo-data-query/dist/npm/filtering/filter-descriptor.interface'
import { GridColumn as Column, Grid, GridSortChangeEvent } from '@progress/kendo-react-grid'
import { CreateFilter } from 'api/Identity/Types/CreateFilter'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { PageData } from 'api/Pagination/Types'
import { IntroducingBrokerProfileDto } from 'api/Trading-Accounts/Types/IntroducingBrokerProfileDto'
import AddButton from 'components/AddButton/AddButton'
import { Pager } from 'components/components/Pager'
import { Modal } from 'components/CustomModal'
import { FilterType } from 'components/Filter'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import { getIntroducingBrokerProfileFilterFields } from 'components/Filter/introducingBrokerProfileFields'
import { Link } from 'components/Link'
import { Spinner } from 'components/Spinner'
import { createGridState, useGridFilter, useGridState, useGridStatusState } from 'features/DataGrid'
import { isGoBack } from 'lib/react-router-dom'
import { useAppDispatch, useAppSelector } from 'store'
import { GridFilterActionState, resetGridFilters, setGridFilters } from 'store/grid'
import {
  fetchFilterAction,
  fetchFilterTypesAction,
  fetchTickmillCompaniesAction,
  removeFilterAction,
  saveFilterAction,
  updateFilterAction,
} from 'store/identity/identity.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { IdentityApiClient } from 'utils/api/identityApi'
import { PartnershipsApiClient } from 'utils/api/partnershipsApi'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { hasPermission } from 'utils/permissionsValidator'
import { buildQuery, buildSortQuery } from 'utils/QueryHelpers'
import { TableWithExport } from 'utils/table/withExport'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import CreateIntroducingBrokerProfilePage from './CreateIntroducingBrokerProfilePage'
import introducingBrokerProfilesColumn from './introducingBrokerProfilesColumn'
import { normalizeIntroducingBrokerProfilesSortQuery } from './normalizer'

const useMapStateToProps = () => ({
  filters: useAppSelector((state) => state.identity.filter),
  filterTypes: useAppSelector((state) => state.identity.filterType),
  tickmillCompanies: useAppSelector((state) => state.identity.tickmillCompanies),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const identityApiClient = useApiClient(IdentityApiClient)

  return {
    fetchFilterTypes: async () => dispatch(fetchFilterTypesAction(identityApiClient)),
    fetchFilters: async () => dispatch(fetchFilterAction(identityApiClient)),
    saveFilter: async (data: CreateFilter) => dispatch(saveFilterAction(data, identityApiClient)),
    updateFilter: async (filterId: string, data: CreateFilter) =>
      dispatch(updateFilterAction(filterId, data, identityApiClient)),
    removeFilter: async (filterId: string) =>
      dispatch(removeFilterAction(filterId, identityApiClient)),
    setGridFilters: async (state: GridFilterActionState) => dispatch(setGridFilters(state)),
    resetGridFilters: async () => dispatch(resetGridFilters()),
    fetchTickmillCompanies: async () => dispatch(fetchTickmillCompaniesAction(identityApiClient)),
  }
}

const IntroducingBrokerProfiles = (): JSX.Element => {
  const partnershipsApiClient = useApiClient(PartnershipsApiClient)

  const [auth] = useContext(AuthSessionContext)
  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })

  const crumbs = [{ title: 'Introducing Broker Profiles', link: links.brokerageIBProfiles.url }]

  const permissions = auth?.permissions || []
  const [profiles, setProfiles] = useState<IntroducingBrokerProfileDto[]>([])
  const [modal, setModal] = useState<boolean>(false)
  const { filters, filterTypes, tickmillCompanies } = useMapStateToProps()
  const { fetchTickmillCompanies, fetchFilterTypes, fetchFilters } = useMapDispatchToProps()

  const { gridState, gridActions } = useGridState()
  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)

  useEffect(() => {
    ;(async () => {
      await fetchGrid(
        async (
          gridFilterState: GridFilterActionState
        ): Promise<PageData<IntroducingBrokerProfileDto[]>> => {
          const searchQuery = getQuery(gridFilterState.filter)
          const sortQuery = getSort(gridFilterState.sort)
          const response = await partnershipsApiClient.getIntroducingBrokersProfiles({
            search: searchQuery,
            sort: sortQuery,
            pageIndex: gridFilterState.pageIndex,
            pageSize: gridFilterState.pageSize,
          })

          if (isEmpty(tickmillCompanies)) await fetchTickmillCompanies()
          if (isEmpty(filterTypes)) await fetchFilterTypes()
          if (isEmpty(filters)) await fetchFilters()

          if (response?.items) {
            setProfiles(response.items)
          }
          setTableData({ ...tableData, meta: response })

          return response
        }
      )
      if (isEmpty(tickmillCompanies)) fetchTickmillCompanies()
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGrid = async (
    callback: (
      gridFilterState: GridFilterActionState
    ) => Promise<PageData<IntroducingBrokerProfileDto[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridFilterState = selectFilterForGoBackHistory(
      createGridState({ skip: 0 }, gridState, { isGoBack })
    )
    const response = await callback(gridFilterState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      return buildQuery(filter ?? gridState.filter) ?? null
    },
    [gridState.filter]
  )

  const getSort = useCallback(
    (sortDescriptors?: SortDescriptor[]): string | null => {
      return buildSortQuery(
        normalizeIntroducingBrokerProfilesSortQuery(sortDescriptors ?? gridState.sort)
      )
    },
    [gridState.sort]
  )

  const filterFetch = useCallback(
    async (params?: { query?: string | null }) => {
      const searchQuery = params?.query !== undefined ? params.query : getQuery()
      const sortQuery = getSort(gridState.sort)
      const pageIndex = 1
      const pageSize = gridState.pageSize ?? 10
      const take = gridState.take ?? 10
      const skip = 0

      const response = await partnershipsApiClient.getIntroducingBrokersProfiles({
        search: searchQuery,
        sort: sortQuery,
        pageIndex,
        pageSize,
        skipCount: tableData?.skipCount !== false,
      })
      const total = response.totalResults

      if (response?.items) {
        setProfiles(response.items)
      }
      gridActions.setGridFilters({
        take,
        skip,
        total,
        pageIndex,
      })
      setTableData({ ...tableData, meta: response })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      getQuery,
      getSort,
      gridActions,
      gridState.pageSize,
      gridState.sort,
      gridState.take,
      tableData?.skipCount,
    ]
  )

  const handleSkipCountChange = async (skipCount: boolean) => {
    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await partnershipsApiClient.getIntroducingBrokersProfiles({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.take,
      skipCount,
    })
    const total = response.totalResults

    if (response?.items) {
      setProfiles(response.items)
    }
    gridActions.setGridFilters({
      total,
    })
    setTableData({ ...tableData, skipCount, meta: response })
  }

  const handleSortChange = async (event: GridSortChangeEvent) => {
    gridActions.setGridFilters({
      sort: event.sort,
    })

    const searchQuery = getQuery(gridState.filter)
    const sortQuery = getSort(event.sort)
    const response = await partnershipsApiClient.getIntroducingBrokersProfiles({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.pageSize,
      skipCount: tableData?.skipCount !== false,
    })

    if (response?.items) {
      setProfiles(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handlePageSizeChange = async (pageSize: number) => {
    await gridActions.setGridFilters({
      ...gridState,
      pageSize,
    })

    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await partnershipsApiClient.getIntroducingBrokersProfiles({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: 1,
      pageSize,
      skipCount: tableData?.skipCount !== false,
    })
    await gridActions.setGridFilters({
      ...gridState,
      pageIndex: 1,
      take: pageSize,
      total: response.totalResults,
    })
    if (response?.items) {
      setProfiles(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleGridPageChange = async (page: number) => {
    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)
    const pageIndex = page
    gridActions.setGridFilters({
      pageIndex,
    })

    const response = await partnershipsApiClient.getIntroducingBrokersProfiles({
      search: searchQuery,
      sort: sortQuery,
      pageIndex,
      pageSize: gridState.take,
      skipCount: tableData?.skipCount !== false,
    })
    const total = response.totalResults

    gridActions.setGridFilters({
      total,
    })
    if (response?.items) {
      setProfiles(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  if (gridStatusState.isLoading) {
    return <Spinner />
  }

  return (
    <React.Fragment>
      {modal && (
        <Modal visible={modal} onClose={() => setModal(false)}>
          <CreateIntroducingBrokerProfilePage
            onClose={() => {
              setModal(false)
              filterFetch()
            }}
          />
        </Modal>
      )}
      <Breadcrumbs crumbs={crumbs} />
      <SelectFiltersFacade
        gridState={gridState}
        gridStatusActions={gridStatusActions}
        gridStatusState={gridStatusState}
        setGridFilters={gridActions.setGridFilters}
        filterFetch={filterFetch}
        filterType={FilterType.INTRODUCING_BROKER_PROFILE}
        filterFields={getIntroducingBrokerProfileFilterFields()}
        containerClassName='wallet-table__filter'
        getQuery={getQuery}
      />
      <AddButton
        disabled={!hasPermission(permissions, PermissionsEnum.CanAddIntroducingBrokerProfile)}
        text='Add IB Profile'
        onClick={() => setModal(true)}
      />
      <TableStateContext.Provider value={[tableData, setTableData]}>
        <TableWithExport data={profiles}>
          <Grid
            {...gridState}
            resizable
            data={profiles}
            sort={gridState.sort}
            total={gridState.total}
            take={gridState.take}
            skip={gridState.skip}
            onSortChange={handleSortChange}
            pageable={true}
            pager={() => (
              <Pager
                onSkipCountChange={handleSkipCountChange}
                onPageChange={handleGridPageChange}
                onPageSizeChange={handlePageSizeChange}
                page={gridState.pageIndex}
                pageSize={gridState.pageSize}
                take={gridState.take || 10}
                skip={gridState.skip || 0}
              />
            )}
            style={{ zIndex: 0 }}
          >
            <Column
              className='table-columns__content'
              title='Name'
              cell={(props) => (
                <td className='table-columns__content'>
                  {hasPermission(permissions, PermissionsEnum.CanViewIntroducingBrokerProfile) ? (
                    <Link
                      to={{
                        pathname: generatePath(links.brokerageIBProfiles.detailsUrl as string, {
                          id: props.dataItem.id,
                          slug: 'details',
                        }),
                      }}
                      style={{ color: 'var(--link)', textDecoration: 'underline' }}
                    >
                      {props.dataItem.name}
                    </Link>
                  ) : (
                    <span>{props.dataItem.name}</span>
                  )}
                </td>
              )}
            />
            {introducingBrokerProfilesColumn
              .filter((c) => c.show)
              .map((column, idx) => (
                <Column key={idx + 2} {...column} />
              ))}
          </Grid>
        </TableWithExport>
      </TableStateContext.Provider>
    </React.Fragment>
  )
}

export default IntroducingBrokerProfiles
